import React, { useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { ChartOptions, Plugin } from 'chart.js';
import { useTheme } from 'providers/ThemeProvider';

type Value = { x: string; y: number | string };

export interface BarChartProps {
  datasets: {
    data: Value[];
    label: string;
    name?: string;
  }[];
  percentage?: boolean;
  colors?: string[];
}

const BarChart = ({ datasets, percentage, colors }: BarChartProps) => {
  const chartRef = useRef<any>(null);
  const { theme } = useTheme();
  const isDarkMode = theme.colorPalette.isDarkMode;

  // Calculate the number of bars (data points)
  const numBars = datasets[0]?.data.length || 0;
  const numDatasets = datasets.length;
  const barWidth = 17;
  const totalBarsWidth = numDatasets * barWidth;
  const groupSpacing = totalBarsWidth + 30;
  const minChartWidth = numBars * groupSpacing + 100; // Add padding

  // Custom plugin to position bars on the left and draw labels
  const barPositionPlugin: Plugin<'bar'> = {
    id: 'barPosition',
    beforeDatasetsDraw(chart) {
      const {
        data,
        chartArea: { left },
      } = chart;

      if (data.datasets.length === 0) return;

      const numDatasets = data.datasets.length;
      const barWidth = 17;
      const totalBarsWidth = numDatasets * barWidth;
      const groupSpacing = totalBarsWidth + 30;
      const startOffset = (groupSpacing - totalBarsWidth) / 2;

      // Position bars for each dataset dynamically
      data.datasets.forEach((_, datasetIndex) => {
        chart.getDatasetMeta(datasetIndex).data.forEach((dataPoint: any, categoryIndex) => {
          // Calculate x position: left edge + group spacing + offset within group
          dataPoint.x = left + groupSpacing * categoryIndex + startOffset + barWidth * datasetIndex + barWidth / 2;
          dataPoint.width = barWidth;
        });
      });
    },
    afterDraw(chart) {
      const {
        ctx,
        data,
        chartArea: { left, bottom },
      } = chart;

      if (data.datasets.length === 0 || !data.datasets[0].data) return;

      ctx.save();
      ctx.font = `9px ${theme.fontFamily}`;
      ctx.fillStyle = isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.grey.grey7;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      const numDatasets = data.datasets.length;
      const barWidth = 17;
      const totalBarsWidth = numDatasets * barWidth;
      const groupSpacing = totalBarsWidth + 30;

      // Draw labels under each bar group
      data.datasets[0].data.forEach((dataPoint: any, index) => {
        const label = dataPoint.x;
        // Position label centered in the middle of the bar group
        const labelX = left + groupSpacing * index + groupSpacing / 2;
        const labelY = bottom + 5;

        ctx.fillText(label, labelX, labelY);
      });

      ctx.restore();
    },
  };

  const data = {
    datasets: datasets.map((dataset, index) => {
      return {
        ...dataset,
        backgroundColor:
          colors?.[index] || (isDarkMode ? theme.colorPalette.turquoise.light : theme.colorPalette.turquoise.normal),
        borderWidth: 2,
        borderColor: 'transparent',
        borderRadius: {
          topLeft: 4,
          topRight: 4,
        },
      };
    }),
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    layout: {
      padding: {
        bottom: 15,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          color: isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.grey.grey3,
        },
        ticks: {
          display: false, // Hide x-axis labels since we're manually positioning
        },
      },
      y: {
        max: percentage ? 100 : undefined,
        grid: {
          color: isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.grey.grey3,
        },
        ticks: {
          callback: (value: number | string) => {
            return percentage ? value + ' %' : value;
          },
          font: {
            size: 9,
            family: theme.fontFamily,
            weight: 'lighter',
          },
          color: isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.grey.grey7,
        },
      },
    },
    plugins: {
      legend: {
        align: 'start',
        labels: {
          boxWidth: 20,
          boxHeight: 10,
          padding: 15,
          font: {
            size: 13,
            family: theme.fontFamily,
            weight: 'lighter',
          },
          color: isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.grey.grey7,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDarkMode ? theme.colorPalette.grey.grey2 : theme.colorPalette.white,
        titleColor: isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.grey.grey7,
        bodyColor: isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.grey.grey7,
        borderColor: isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.grey.grey3,
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        boxWidth: 15,
        boxHeight: 15,
        usePointStyle: false,
        titleFont: {
          size: 13,
          family: theme.fontFamily,
          weight: 'bold',
        },
        bodyFont: {
          size: 12,
          family: theme.fontFamily,
          weight: 'normal',
        },
        footerFont: {
          size: 12,
          family: theme.fontFamily,
          weight: 'bold',
        },
        footerColor: isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.grey.grey7,
        filter: (tooltipItem) => {
          const dataPoint = tooltipItem.raw as Value;
          const xValue = dataPoint.x;
          const dataset = tooltipItem.dataset;
          const dataArray = dataset.data as unknown as Value[];
          return dataArray.some((d) => d.x === xValue && d.y !== 0 && d.y !== '' && d.y !== null);
        },
        callbacks: {
          title: (tooltipItems) => {
            if (tooltipItems.length > 0) {
              const dataPoint = tooltipItems[0].raw as Value;
              return dataPoint.x;
            }
            return '';
          },
          label: (tooltipItem) => {
            const dataset = tooltipItem.dataset;
            const value = (tooltipItem.raw as Value).y;
            const formattedValue = typeof value === 'number' ? value.toLocaleString() : value;
            return `${dataset.label}       ${formattedValue}`;
          },
          footer: (tooltipItems) => {
            let sum = 0;
            tooltipItems.forEach((tooltipItem) => {
              const value = (tooltipItem.raw as Value).y;
              sum += typeof value === 'number' ? value : parseFloat(String(value)) || 0;
            });
            return `Total:       ${sum.toLocaleString()}`;
          },
        },
      },
    },
  };

  return (
    <div style={{ width: '100%', overflowX: 'auto', height: '100%' }}>
      <div style={{ minWidth: `${minChartWidth}px`, height: '100%' }}>
        <Bar ref={chartRef} data={data} options={options} plugins={[barPositionPlugin]} />
      </div>
    </div>
  );
};

export default BarChart;
